// src/lib/forfeoCanonicalIdentity.ts
//
// FORFEO INC. — Lot 4A
//
// Contrat canonique partagé entre Explore et Giftshop.
//
// Règles fondamentales :
// - le navigateur n’est jamais l’autorité finale de l’identité;
// - un rôle inconnu ne reçoit aucun privilège;
// - les rôles ambigus échouent fermés;
// - les permissions sensibles doivent être confirmées côté serveur.

export const FORFEO_CANONICAL_ROLES = [
  'user',
  'merchant',
  'corporate',
  'admin',
  'support',
  'prp',
  'affiliate',
  'habitat_landlord',
  'habitat_tenant',
  'forfeopro',
  'employee',
] as const;

export type ForfeoCanonicalRole =
  (typeof FORFEO_CANONICAL_ROLES)[number];

export const FORFEO_ONBOARDING_STATUSES = [
  'not_started',
  'in_progress',
  'submitted',
  'under_review',
  'approved',
  'rejected',
  'suspended',
] as const;

export type ForfeoOnboardingStatus =
  (typeof FORFEO_ONBOARDING_STATUSES)[number];

export const FORFEO_PERMISSIONS = [
  'consumer:access',
  'merchant:access',
  'merchant:manage',
  'corporate:access',
  'platform:admin',
  'platform:support',
  'prp:access',
  'affiliate:access',
  'habitat:landlord',
  'habitat:tenant',
  'forfeopro:access',
  'employee:access',
] as const;

export type ForfeoPermission =
  (typeof FORFEO_PERMISSIONS)[number];

export type ForfeoPortalId =
  | 'consumer'
  | 'merchant'
  | 'corporate'
  | 'admin'
  | 'support'
  | 'prp'
  | 'affiliate'
  | 'habitat_landlord'
  | 'habitat_tenant'
  | 'forfeopro'
  | 'employee';

export const FORFEO_AMBIGUOUS_ROLE_VALUES = [
  'owner',
  'closer',
  'habitat',
] as const;

const ROLE_ALIASES: Readonly<Record<string, ForfeoCanonicalRole>> = {
  user: 'user',
  utilisateur: 'user',
  client: 'user',
  customer: 'user',
  traveler: 'user',
  traveller: 'user',
  voyageur: 'user',

  merchant: 'merchant',
  marchand: 'merchant',
  commercant: 'merchant',
  commerçant: 'merchant',
  business: 'merchant',

  corporate: 'corporate',
  enterprise: 'corporate',
  entreprise: 'corporate',
  company: 'corporate',
  b2b: 'corporate',

  admin: 'admin',
  administrator: 'admin',
  administrateur: 'admin',
  super_admin: 'admin',
  platform_admin: 'admin',
  forfeo_admin: 'admin',
  forfeo_super_admin: 'admin',
  pulse_admin: 'admin',

  support: 'support',
  moderator: 'support',
  moderateur: 'support',
  modérateur: 'support',

  prp: 'prp',
  pulse_prp: 'prp',

  affiliate: 'affiliate',
  ambassador: 'affiliate',
  ambassadeur: 'affiliate',
  partner: 'affiliate',
  partenaire: 'affiliate',

  habitat_landlord: 'habitat_landlord',
  landlord: 'habitat_landlord',
  proprietaire: 'habitat_landlord',
  propriétaire: 'habitat_landlord',

  habitat_tenant: 'habitat_tenant',
  tenant: 'habitat_tenant',
  locataire: 'habitat_tenant',

  forfeopro: 'forfeopro',
  forfeo_pro: 'forfeopro',

  employee: 'employee',
  employe: 'employee',
  employé: 'employee',
  staff: 'employee',
};

const ROLE_PERMISSIONS: Readonly<
  Record<ForfeoCanonicalRole, readonly ForfeoPermission[]>
> = {
  user: [
    'consumer:access',
  ],

  merchant: [
    'consumer:access',
    'merchant:access',
    'merchant:manage',
  ],

  corporate: [
    'consumer:access',
    'corporate:access',
  ],

  admin: [
    ...FORFEO_PERMISSIONS,
  ],

  support: [
    'consumer:access',
    'platform:support',
  ],

  prp: [
    'consumer:access',
    'prp:access',
  ],

  affiliate: [
    'consumer:access',
    'affiliate:access',
  ],

  habitat_landlord: [
    'consumer:access',
    'habitat:landlord',
  ],

  habitat_tenant: [
    'consumer:access',
    'habitat:tenant',
  ],

  forfeopro: [
    'consumer:access',
    'forfeopro:access',
  ],

  employee: [
    'consumer:access',
    'merchant:access',
    'employee:access',
  ],
};

const ROLE_PORTALS: Readonly<
  Record<ForfeoCanonicalRole, ForfeoPortalId>
> = {
  user: 'consumer',
  merchant: 'merchant',
  corporate: 'corporate',
  admin: 'admin',
  support: 'support',
  prp: 'prp',
  affiliate: 'affiliate',
  habitat_landlord: 'habitat_landlord',
  habitat_tenant: 'habitat_tenant',
  forfeopro: 'forfeopro',
  employee: 'employee',
};

const ROLE_DESTINATIONS: Readonly<
  Record<ForfeoCanonicalRole, string>
> = {
  user: '/club',
  merchant: '/business-dashboard',
  corporate: '/corporate/dashboard',
  admin: '/admin-forfeo-portal',
  support: '/aide',
  prp: '/prp-dashboard',
  affiliate: '/affiliate-dashboard',
  habitat_landlord: '/habitat/proprio/dashboard',
  habitat_tenant: '/habitat/locataire/dashboard',
  forfeopro: '/forfeopro/dashboard',
  employee: '/business-dashboard',
};

export interface ForfeoCanonicalIdentity {
  userId: string;
  role: ForfeoCanonicalRole;
  onboardingStatus: ForfeoOnboardingStatus;
  portal: ForfeoPortalId;
  destination: string;
  permissions: readonly ForfeoPermission[];
  browserAuthoritative: false;
}

function normalizedToken(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .trim()
    .toLowerCase()
    .replaceAll('-', '_')
    .replaceAll(' ', '_');
}

export function normalizeForfeoRole(
  value: unknown,
): ForfeoCanonicalRole | null {
  const token = normalizedToken(value);

  if (!token) {
    return null;
  }

  if (
    FORFEO_AMBIGUOUS_ROLE_VALUES.includes(
      token as (typeof FORFEO_AMBIGUOUS_ROLE_VALUES)[number],
    )
  ) {
    return null;
  }

  return ROLE_ALIASES[token] ?? null;
}

export function normalizeForfeoOnboardingStatus(
  value: unknown,
): ForfeoOnboardingStatus {
  const token = normalizedToken(value);

  return FORFEO_ONBOARDING_STATUSES.includes(
    token as ForfeoOnboardingStatus,
  )
    ? token as ForfeoOnboardingStatus
    : 'not_started';
}

export function permissionsForForfeoRole(
  role: ForfeoCanonicalRole,
): readonly ForfeoPermission[] {
  return ROLE_PERMISSIONS[role];
}

export function hasForfeoPermission(
  role: ForfeoCanonicalRole,
  permission: ForfeoPermission,
): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function portalForForfeoRole(
  role: ForfeoCanonicalRole,
): ForfeoPortalId {
  return ROLE_PORTALS[role];
}

export function destinationForForfeoRole(
  role: ForfeoCanonicalRole,
): string {
  return ROLE_DESTINATIONS[role];
}

export function createForfeoCanonicalIdentity(input: {
  userId: unknown;
  role: unknown;
  onboardingStatus?: unknown;
}): ForfeoCanonicalIdentity | null {
  const userId =
    typeof input.userId === 'string'
      ? input.userId.trim()
      : '';

  const role = normalizeForfeoRole(input.role);

  if (!userId || !role) {
    return null;
  }

  return {
    userId,
    role,
    onboardingStatus:
      normalizeForfeoOnboardingStatus(
        input.onboardingStatus,
      ),
    portal: portalForForfeoRole(role),
    destination: destinationForForfeoRole(role),
    permissions: permissionsForForfeoRole(role),
    browserAuthoritative: false,
  };
}

export const FORFEO_BROWSER_IDENTITY_IS_AUTHORITATIVE = false;
